import {useCallback} from 'react'

import {MemexColumnDataType} from '../../api/columns/contracts/memex-column'
import {intervalDatesDescription, isCurrentIteration} from '../../helpers/iterations'
import {ViewerPrivileges} from '../../helpers/viewer-privileges'
import {useDeleteGroup} from '../../hooks/use-delete-group'
import type {ColumnModel} from '../../models/column-model'
import type {
  HorizontalGroup,
  ItemsGroupedBySwimlaneCell,
  ItemsGroupedByVerticalColumn,
} from '../../models/horizontal-group'
import type {MemexItemModel} from '../../models/memex-item-model'
import {isIteration, MissingVerticalGroupId, type VerticalGroup} from '../../models/vertical-group'
import {ColumnsPagination} from './board-pagination'
import {Column} from './column/column'
import type {ColumnHeaderType} from './column/types'
import {useUpdateBoardColumnDetails} from './hooks/use-update-board-column-details'

const defaultItemsGroupedByFieldPerOption: ReadonlyArray<MemexItemModel> = []
export function Columns({
  groupByFieldOptions,
  itemsGroupedByField,
  groupByField,
  isLoading = false,
  showItemsSkeleton = false,
  scrollToItemId,
  lastNewColumnNameRef,
  horizontalGroup,
  horizontalGroupIndex,
  headerType = 'visible',
  hideColumn,
  isLastGroup,
}: {
  groupByFieldOptions: ReadonlyArray<VerticalGroup>
  itemsGroupedByField: ItemsGroupedByVerticalColumn | ItemsGroupedBySwimlaneCell
  groupByField?: ColumnModel
  isLoading?: boolean
  showItemsSkeleton?: boolean
  scrollToItemId?: number
  lastNewColumnNameRef: React.RefObject<string | null>
  horizontalGroup?: HorizontalGroup
  headerType?: ColumnHeaderType
  hideColumn: (option: VerticalGroup) => boolean
  isLastGroup?: boolean
  horizontalGroupIndex: number
}) {
  const {hasWritePermissions} = ViewerPrivileges()
  const {updateColumnDetails} = useUpdateBoardColumnDetails(groupByField)
  const {deleteGroup} = useDeleteGroup(groupByField)

  const addColumnPaginator = !(isLoading && groupByFieldOptions.length)
  /**
   * Returns whether the current `verticalGroup` corresponds to
   * an `Iteration` data type whose value is equal to the "current"
   * iteration - i.e. an iteration that is in progress date-wise
   */
  const isVerticalGroupCurrentIteration = useCallback(
    (verticalGroup: VerticalGroup): boolean => {
      if (!groupByField) return false

      if (groupByField.dataType !== MemexColumnDataType.Iteration) return false
      if (!isIteration(verticalGroup.groupMetadata)) return false
      return isCurrentIteration(new Date(), verticalGroup.groupMetadata)
    },
    [groupByField],
  )

  return (
    <>
      {groupByFieldOptions.map((verticalGroup, index) => {
        const group = itemsGroupedByField[verticalGroup.id]
        const filteredItemsFromField = group?.items ?? defaultItemsGroupedByFieldPerOption
        const isEditable = hasWritePermissions && verticalGroup.id !== MissingVerticalGroupId
        const isDraggable = groupByField?.dataType !== MemexColumnDataType.Iteration
        const totalCount = group && 'totalCount' in group ? group.totalCount : undefined

        return (
          <Column
            key={verticalGroup.id}
            hidden={hideColumn(verticalGroup)}
            verticalGroup={verticalGroup}
            groupId={group?.groupId}
            totalCount={totalCount}
            items={filteredItemsFromField}
            showItemsSkeleton={showItemsSkeleton}
            index={index}
            onDelete={deleteGroup}
            onUpdateDetails={updateColumnDetails}
            scrollToItemId={scrollToItemId}
            initialNameFocus={lastNewColumnNameRef.current === verticalGroup.name}
            // TODO: Change this to something that ensures it's an editable column
            // instead of hard-coded MissingStatusColumn.Id.
            isUserEditable={isEditable}
            isDraggable={isDraggable}
            isCurrentIteration={isVerticalGroupCurrentIteration(verticalGroup)}
            iterationDateRange={
              isIteration(verticalGroup.groupMetadata)
                ? (intervalDatesDescription(verticalGroup.groupMetadata) ?? '')
                : ''
            }
            horizontalGroup={horizontalGroup}
            headerType={headerType}
            isLastGroup={isLastGroup}
            horizontalGroupIndex={horizontalGroupIndex}
          />
        )
      })}
      {addColumnPaginator && <ColumnsPagination headerType={headerType} />}
    </>
  )
}

try{ Columns.displayName ||= 'Columns' } catch {}